import { getLocale, replacePlaceholders, resolveDateFormat } from '@msc-b2c/shared-utils';
import { ItineraryDateRangeFormats } from '@msc-b2c/interfaces/types';
import dayjs from 'dayjs';

const getSafeIntlLocale = (): string | undefined => {
  const locale = getLocale();
  if (!locale) return undefined;

  // AEM can provide locales like en_GB, but Intl expects BCP-47 tags (en-GB).
  const normalizedLocale = locale.replace('_', '-');

  try {
    return Intl.getCanonicalLocales(normalizedLocale)[0] ?? undefined;
  } catch {
    return undefined;
  }
};

export const formatDateRangeForItinerary = (
  startDate: Date,
  endDate: Date,
  formats: Partial<ItineraryDateRangeFormats> = {}
) => {
  const locale = getSafeIntlLocale();
  const standardDateFormat = resolveDateFormat();
  const formatStandardDate = (date: Date) => {
    const parsedDate = dayjs(date);
    return locale
      ? parsedDate.locale(locale).format(standardDateFormat)
      : parsedDate.format(standardDateFormat);
  };
  const standardDateRange = `${formatStandardDate(startDate)} - ${formatStandardDate(endDate)}`;

  const startDay = startDate.getDate();
  const endDay = endDate.getDate();

  const startMonth = startDate.toLocaleString(locale, { month: 'short' });
  const endMonth = endDate.toLocaleString(locale, { month: 'short' });

  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  const sameMonthSameYearTemplate = formats?.sameMonthSameYear;
  const differentMonthSameYearTemplate = formats?.differentMonthSameYear;
  const differentYearTemplate = formats?.differentYear;

  const placeholderValues = {
    startDay: String(startDay),
    endDay: String(endDay),
    startMonth,
    endMonth,
    startYear: String(startYear),
    endYear: String(endYear),
  };

  if (startMonth === endMonth && startYear === endYear) {
    // Same month and year
    return sameMonthSameYearTemplate
      ? replacePlaceholders(sameMonthSameYearTemplate, placeholderValues)
      : standardDateRange;
  } else if (startYear === endYear) {
    // Different months, same year
    return differentMonthSameYearTemplate
      ? replacePlaceholders(differentMonthSameYearTemplate, placeholderValues)
      : standardDateRange;
  } else {
    // Different years
    return differentYearTemplate
      ? replacePlaceholders(differentYearTemplate, placeholderValues)
      : standardDateRange;
  }
};
